import { Alert, Button, Card, Field, Input, Modal, Select, Textarea } from "@aman/design-system";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, asApiError, type ApiError } from "../api/client";
import { useApiQuery } from "../api/hooks";
import { ApiErrorAlert, DataTable, LoadingTable, PageHeader, SearchBar, StatusBadge, useFilteredRows } from "../components/common";
import { listFromPayload, stringValue, type AnyRecord } from "../types";

type ApplianceCommand = "collect-diagnostics" | "restart-service" | "update" | "reboot";
type PendingAction =
  | { kind: "approve"; appliance: AnyRecord }
  | { kind: "reject"; appliance: AnyRecord }
  | { kind: "reEnroll"; appliance: AnyRecord }
  | { kind: "delete"; appliance: AnyRecord }
  | { kind: "command"; appliance: AnyRecord; command: ApplianceCommand };

const safeCommands: Array<{ type: ApplianceCommand; labelKey: string; dangerous?: boolean }> = [
  { type: "collect-diagnostics", labelKey: "diagnostics" },
  { type: "restart-service", labelKey: "restartRadius" },
  { type: "update", labelKey: "update" },
  { type: "reboot", labelKey: "reboot", dangerous: true },
];

function idOf(record: AnyRecord): string {
  return stringValue(record["publicId"], stringValue(record["applianceId"], stringValue(record["id"])));
}

function productKey(record: AnyRecord): string {
  return stringValue(record["productKey"], stringValue(record["product_key"]));
}

function applianceName(record: AnyRecord): string {
  return stringValue(record["displayName"], stringValue(record["name"], stringValue(record["hostname"], idOf(record))));
}

function commandPayload(command: ApplianceCommand): Record<string, unknown> {
  if (command === "restart-service") return { service: "radius" };
  if (command === "collect-diagnostics") return { scope: "services" };
  return {};
}

export function AppliancesPage() {
  const { t } = useTranslation();
  const appliances = useApiQuery<unknown>("/appliances");
  const licenses = useApiQuery<unknown>("/licenses");
  const [search, setSearch] = useState("");
  const [action, setAction] = useState<PendingAction>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const [success, setSuccess] = useState<string>();
  const rows = useFilteredRows(listFromPayload(appliances.data), search);
  const licenseRows = listFromPayload(licenses.data);

  const reload = async (): Promise<void> => { await Promise.all([appliances.reload(), licenses.reload()]); };
  const close = (): void => { if (!busy) setAction(undefined); };
  const perform = async (path: string, payload: Record<string, unknown>): Promise<void> => {
    setBusy(true); setError(undefined); setSuccess(undefined);
    try {
      await api.post(path, payload);
      setAction(undefined); setSuccess(t("pages.appliances.requestQueued")); await reload();
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };
  const performDelete = async (path: string, payload: Record<string, unknown>): Promise<void> => {
    setBusy(true); setError(undefined); setSuccess(undefined);
    try {
      await api.delete(path, payload);
      setAction(undefined); setSuccess(t("pages.appliances.deleted")); await reload();
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };

  const rowActions = (record: AnyRecord) => {
    const status = stringValue(record["status"]).toLowerCase();
    if (status === "pending") return <>
      <Button type="button" size="small" onClick={() => setAction({ kind: "approve", appliance: record })}>{t("pages.appliances.approve")}</Button>
      <Button type="button" size="small" variant="danger" onClick={() => setAction({ kind: "reject", appliance: record })}>{t("pages.appliances.reject")}</Button>
      <Button type="button" size="small" variant="danger" onClick={() => setAction({ kind: "delete", appliance: record })}>{t("pages.appliances.deletePermanently")}</Button>
    </>;
    if (status !== "approved" && status !== "active") return <Button type="button" size="small" variant="danger"
      onClick={() => setAction({ kind: "delete", appliance: record })}>{t("pages.appliances.deletePermanently")}</Button>;
    const reEnrollHash = stringValue(record["reEnrollPublicKeyHash"]);
    return <>{reEnrollHash !== "" ? <Button type="button" size="small" variant="danger"
      onClick={() => setAction({ kind: "reEnroll", appliance: record })}>{t("pages.appliances.reEnroll")}</Button> : null}
      {safeCommands.map((command) => <Button type="button" size="small" variant={command.dangerous === true ? "danger" : "secondary"}
      key={command.type} onClick={() => setAction({ kind: "command", appliance: record, command: command.type })}>
      {t(`pages.appliances.${command.labelKey}`)}
    </Button>)}
      <Button type="button" size="small" variant="danger" onClick={() => setAction({ kind: "delete", appliance: record })}>{t("pages.appliances.deletePermanently")}</Button>
    </>;
  };

  return <div className="page-stack">
    <PageHeader title={t("pages.appliances.title")} subtitle={t("pages.appliances.subtitle")} />
    <ApiErrorAlert error={appliances.error ?? licenses.error ?? error} onRetry={() => void reload()} />
    {success !== undefined ? <Alert tone="success">{success}</Alert> : null}
    <Card bodyClassName="card-body--flush">
      <SearchBar value={search} onChange={setSearch} onRefresh={() => void appliances.reload()} loading={appliances.loading} />
      {appliances.loading && appliances.data === undefined ? <LoadingTable columns={9} /> : <DataTable rows={rows} columns={[
        { key: "displayName", label: t("pages.appliances.displayName"), render: (row) => applianceName(row) },
        { key: "hostname", label: t("pages.appliances.hostname"), render: (row) => stringValue(row["hostname"], "—") },
        { key: "claimCode", label: t("pages.appliances.claimCode"), render: (row) => <code dir="ltr">{stringValue(row["claimCode"], "—")}</code> },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "deviceId", label: t("pages.appliances.deviceId"), render: (row) => <code dir="ltr">{stringValue(row["deviceId"], "—")}</code> },
        { key: "licenseId", label: t("nav.licenses"), render: (row) => stringValue(row["licenseId"], "—") },
        { key: "lastIp", label: t("pages.appliances.ipAddress"), render: (row) => <code dir="ltr">{stringValue(row["lastIp"], "—")}</code> },
        { key: "applianceVersion", label: t("common.version"), render: (row) => stringValue(row["applianceVersion"], "—") },
        { key: "lastSeenAt", label: t("pages.appliances.lastSeen"), render: (row) => stringValue(row["lastSeenAt"], "—") },
      ]} actions={rowActions} />}
    </Card>
    {action?.kind === "approve" ? <ApproveModal appliance={action.appliance} licenses={licenseRows} busy={busy} error={error} onClose={close}
      onSubmit={(licenseId, displayName) => perform(`/appliances/${encodeURIComponent(idOf(action.appliance))}/approve`, { licenseId, name: displayName })} /> : null}
    {action?.kind === "reject" ? <ReasonModal title={t("pages.appliances.reject")} busy={busy} error={error} onClose={close}
      onSubmit={(reason) => perform(`/appliances/${encodeURIComponent(idOf(action.appliance))}/reject`, { reason })} /> : null}
    {action?.kind === "reEnroll" ? <ReEnrollModal appliance={action.appliance} busy={busy} error={error} onClose={close}
      onSubmit={(reason, confirmation) => perform(`/appliances/${encodeURIComponent(idOf(action.appliance))}/re-enroll`, {
        reason, confirmApplianceId: confirmation,
        newPublicKeyHash: stringValue(action.appliance["reEnrollPublicKeyHash"]),
      })} /> : null}
    {action?.kind === "delete" ? <DeleteApplianceModal appliance={action.appliance} busy={busy} error={error} onClose={close}
      onSubmit={(reason, confirmation) => performDelete(`/appliances/${encodeURIComponent(idOf(action.appliance))}`, {
        reason, confirmApplianceId: confirmation,
      })} /> : null}
    {action?.kind === "command" ? <CommandModal command={action.command} appliance={action.appliance} busy={busy} error={error} onClose={close}
      onSubmit={(reason) => perform(`/appliances/${encodeURIComponent(idOf(action.appliance))}/commands`, {
        type: action.command, payload: commandPayload(action.command), reason,
      })} /> : null}
  </div>;
}

function DeleteApplianceModal({ appliance, busy, error, onClose, onSubmit }: {
  appliance: AnyRecord; busy: boolean; error: ApiError | undefined; onClose: () => void;
  onSubmit: (reason: string, confirmation: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const applianceId = idOf(appliance);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (reason.trim().length >= 3 && confirmation === applianceId) void onSubmit(reason.trim(), confirmation);
  };
  return <Modal open title={t("pages.appliances.deleteTitle")} onClose={onClose}>
    <ApiErrorAlert error={error} />
    <Alert tone="warning">{t("pages.appliances.deleteWarning")}</Alert>
    <form className="form-grid" onSubmit={submit}>
      <Field className="form-grid__full" label={t("common.reason")}><Textarea required minLength={3} maxLength={500} rows={3} value={reason} onChange={(event) => setReason(event.target.value)} /></Field>
      <Field className="form-grid__full" label={t("pages.appliances.confirmAppliance", { id: applianceId })}><Input required dir="ltr" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></Field>
      <div className="form-actions form-grid__full"><Button type="button" variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button><Button type="submit" variant="danger" busy={busy} disabled={reason.trim().length < 3 || confirmation !== applianceId}>{t("pages.appliances.deletePermanently")}</Button></div>
    </form>
  </Modal>;
}

function ReEnrollModal({ appliance, busy, error, onClose, onSubmit }: {
  appliance: AnyRecord; busy: boolean; error: ApiError | undefined; onClose: () => void;
  onSubmit: (reason: string, confirmation: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const applianceId = idOf(appliance);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (reason.trim().length >= 3 && confirmation === applianceId) void onSubmit(reason.trim(), confirmation);
  };
  return <Modal open title={t("pages.appliances.reEnrollTitle")} onClose={onClose}>
    <ApiErrorAlert error={error} />
    <Alert tone="warning">{t("pages.appliances.reEnrollWarning")}</Alert>
    <dl>
      <dt>{t("pages.appliances.newKeyHash")}</dt><dd><code dir="ltr">{stringValue(appliance["reEnrollPublicKeyHash"])}</code></dd>
      <dt>{t("pages.appliances.candidateIp")}</dt><dd><code dir="ltr">{stringValue(appliance["reEnrollLastIp"], "—")}</code></dd>
      <dt>{t("pages.appliances.candidateLastSeen")}</dt><dd>{stringValue(appliance["reEnrollLastSeenAt"], "—")}</dd>
    </dl>
    <form className="form-grid" onSubmit={submit}>
      <Field className="form-grid__full" label={t("common.reason")}><Textarea required minLength={3} maxLength={500} rows={3} value={reason} onChange={(event) => setReason(event.target.value)} /></Field>
      <Field className="form-grid__full" label={t("pages.appliances.confirmAppliance", { id: applianceId })}><Input required dir="ltr" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></Field>
      <div className="form-actions form-grid__full"><Button type="button" variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button><Button type="submit" variant="danger" busy={busy} disabled={reason.trim().length < 3 || confirmation !== applianceId}>{t("pages.appliances.reEnroll")}</Button></div>
    </form>
  </Modal>;
}

function ApproveModal({ appliance, licenses, busy, error, onClose, onSubmit }: {
  appliance: AnyRecord; licenses: AnyRecord[]; busy: boolean; error: ApiError | undefined;
  onClose: () => void; onSubmit: (licenseId: string, displayName: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const matchingLicenses = useMemo(() => licenses.filter((license) => {
    return stringValue(license["status"]) === "active" && productKey(license) === "max-radius";
  }), [appliance, licenses]);
  const [licenseId, setLicenseId] = useState("");
  const [displayName, setDisplayName] = useState(applianceName(appliance));
  const submit = (event: FormEvent): void => { event.preventDefault(); if (licenseId !== "" && displayName.trim() !== "") void onSubmit(licenseId, displayName.trim()); };
  return <Modal open title={t("pages.appliances.approveTitle")} onClose={onClose}>
    <ApiErrorAlert error={error} /><Alert tone="warning">{t("pages.appliances.approveWarning")}</Alert>
    <form className="form-grid" onSubmit={submit}>
      <Field label={t("pages.appliances.displayName")}><Input required maxLength={200} value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></Field>
      <Field label={t("nav.licenses")}><Select required value={licenseId} onChange={(event) => setLicenseId(event.target.value)}>
        <option value="">{t("common.select")}</option>
        {matchingLicenses.map((license) => { const id = idOf(license); return <option value={id} key={id}>{`${stringValue(license["customerName"], "—")} · ${stringValue(license["productName"], productKey(license))} · ${id}`}</option>; })}
      </Select></Field>
      <div className="form-actions form-grid__full"><Button type="button" variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button><Button type="submit" busy={busy} disabled={licenseId === "" || displayName.trim() === ""}>{t("pages.appliances.approve")}</Button></div>
    </form>
  </Modal>;
}

function ReasonModal({ title, busy, error, onClose, onSubmit }: { title: string; busy: boolean; error: ApiError | undefined; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const { t } = useTranslation(); const [reason, setReason] = useState("");
  const submit = (event: FormEvent): void => { event.preventDefault(); if (reason.trim().length >= 3) void onSubmit(reason.trim()); };
  return <Modal open title={title} onClose={onClose}><ApiErrorAlert error={error} /><form className="form-grid" onSubmit={submit}>
    <Field className="form-grid__full" label={t("common.reason")}><Textarea required minLength={3} maxLength={500} rows={4} value={reason} onChange={(event) => setReason(event.target.value)} /></Field>
    <div className="form-actions form-grid__full"><Button type="button" variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button><Button type="submit" variant="danger" busy={busy} disabled={reason.trim().length < 3}>{t("common.submit")}</Button></div>
  </form></Modal>;
}

function CommandModal({ command, appliance, busy, error, onClose, onSubmit }: { command: ApplianceCommand; appliance: AnyRecord; busy: boolean; error: ApiError | undefined; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const { t } = useTranslation(); const labelKey = safeCommands.find((item) => item.type === command)?.labelKey ?? command; const [reason, setReason] = useState("");
  const submit = (event: FormEvent): void => { event.preventDefault(); if (reason.trim().length >= 3) void onSubmit(reason.trim()); };
  return <Modal open title={t("pages.appliances.commandTitle")} onClose={onClose}><ApiErrorAlert error={error} />
    <Alert tone={command === "reboot" ? "warning" : "info"}>{t("pages.appliances.commandSummary", { command: t(`pages.appliances.${labelKey}`), device: applianceName(appliance) })}</Alert>
    <form className="form-grid" onSubmit={submit}><Field className="form-grid__full" label={t("common.reason")}><Textarea required minLength={3} maxLength={500} rows={4} value={reason} onChange={(event) => setReason(event.target.value)} /></Field>
      <div className="form-actions form-grid__full"><Button type="button" variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button><Button type="submit" variant={command === "reboot" ? "danger" : "primary"} busy={busy} disabled={reason.trim().length < 3}>{t("common.submit")}</Button></div>
    </form>
  </Modal>;
}
